import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";

// Bootstrap components for modal & buttons
import { Modal, Button } from "react-bootstrap";

// Font Awesome for icons
import "font-awesome/css/font-awesome.min.css";

// Default service types (will be overridden by fetched data)
const defaultServiceTypeList = {
  "0": "Food Pantry",
  "1": "Financial Assistance",
  "2": "Medical Services",
  "3": "Transportation Services",
  "4": "Laundry Services",
};

function OrganizationList({ onSelect = () => {} }) {
  const [serviceTypeList, setServiceTypeList] = useState(defaultServiceTypeList);
  // =========================================================================
  // STATE MANAGEMENT
  // =========================================================================

  // SEARCH/FILTER STATE
  const [searchName, setSearchName] = useState("");
  const [quickService, setQuickService] = useState(""); // single service id ("0"-"4") or ""
  const [selectedServices, setSelectedServices] = useState([]); // array of ids ["0","2"]
  const [matchMode, setMatchMode] = useState("any"); // "any" | "all"

  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  
  const [organizations, setOrganizations] = useState([]);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [selectedOrgDetails, setSelectedOrgDetails] = useState(null);
  const [loadingOrgDetails, setLoadingOrgDetails] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  
  // STATE FOR USER AUTHORIZATION AND LOADING
  const [userRole, setUserRole] = useState(null);
  
  // The OrgID the current user is an admin for (from the OrgID cookie)
  const [userOrgID, setUserOrgID] = useState(null);
  
  // Tracks if authorization data has been fetched
  const [authLoading, setAuthLoading] = useState(true);
  
  // STATE FOR EDIT MODAL
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({ hours: {}, timeZone: "", services: [] });
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");

  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BACKURL;
  const url = baseUrl + "/api/foodPantry/organization";

  // Fetch available service types when edit modal opens
  useEffect(() => {
    if (!showEditModal) return;

    (async () => {
      try {
        const res = await axios.get(
          `${baseUrl}/api/foodPantry/event/serviceTypes`,
          { withCredentials: true }
        );
        const fetchedTypes = {};
        res.data.forEach((st) => {
          fetchedTypes[String(st.serviceTypeID)] = st.name;
        });
        setServiceTypeList(fetchedTypes);
      } catch (e) {
        console.error("Failed to load service types:", e);
        // Keep using default if fetch fails
      }
    })();
  }, [showEditModal, baseUrl]);

  // =========================================================================
  // AUTHORIZATION LOGIC
  // =========================================================================

  /**
   * Determines if the "Edit Details" button should be displayed for the selected organization.
   * Logic: superAdmin (0) can edit any. orgAdmin (1) can only edit their own.
   * @param {string} role - The user's role ID (e.g., "0", "1").
   * @param {string} userOrgId - The organization ID the user is an admin for.
   * @param {object} selectedOrg - The currently selected organization details.
   * @returns {boolean} True if the user is authorized to edit the organization.
   */
  const canEditOrganization = (role, userOrgId, selectedOrg) => {
    if (!selectedOrg || !selectedOrg.orgID) return false;
    
    // 1. superAdmin (role "0") can edit any organization
    if (role === "0") {
        return true;
    }

    // 2. orgAdmin (role "1") can only edit their own organization
    if (role === "1") {
        // Compare the current user's OrgID (from cookie/state - UUID string) 
        // against the selected organization's OrgID (UUID string)
        return userOrgId === selectedOrg.orgID;
    }

    // 3. All others cannot edit
    return false;
  };

  // =========================================================================
  // USE EFFECTS (Initial Load)
  // =========================================================================

  /**
   * Fetch user role and set userOrgID from cookie on load.
   * Used for determining authorization (e.g., showing the Edit button).
   */
  useEffect(() => {
    async function fetchUserAuthInfo() {
        const sessionID = Cookies.get("sessionID");
        
        // Get the current user's organization ID from the cookie
        const orgIDFromCookie = Cookies.get("OrgID"); 

        if (!sessionID) {
            setAuthLoading(false);
            return navigate("/SignIn");
        }

        try {
            // API call to get the current user's role
            const roleUrl = `${baseUrl}/api/foodPantry/user/role`; 
            const response = await axios.get(roleUrl, { withCredentials: true });
            const role  = response.data.roleID;
            
            setUserRole(role);
            setUserOrgID(orgIDFromCookie || null);
        } catch (error) {
            console.error("Error fetching user role:", error);
            
            // On error, default to minimal access
            setUserRole(null); 
            setUserOrgID(null);
        } finally {
            // Crucial: set loading to false after attempting to fetch
            setAuthLoading(false);
        }
    }
    fetchUserAuthInfo();
  }, [navigate, baseUrl]);

  // Effect to fetch initial organization list
  useEffect(() => {
    async function getOrgNames() {
      try {
        const sessionID = Cookies.get("sessionID");

        if (!sessionID) {
          alert("Session expired. Please log in again.");
          navigate("/SignIn");
          return;
        }

        const response = await axios.get(url, {
          withCredentials: true, // Ensures cookies are sent
        });

        // Data from getOrganizations includes addresses, phones, and hours
        const orgs = response.data.OrgNames || [];

        orgs.sort((a, b) => {
          const nameA = (a.name || "").toUpperCase();
          const nameB = (b.name || "").toUpperCase();

          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });

        setOrganizations(orgs);
      } catch (error) {
        console.error("Error fetching organizations", error);
      }
    }

    getOrgNames();
  }, [navigate, url]);

  // =========================================================================
  // HELPER FUNCTIONS (Formatting & Initialization)
  // =========================================================================

  /**
   * Helper to format times from 24-hour (e.g., "17:00:00") to 12-hour (e.g., "5:00 PM").
   * @param {string} value - Time string in HH:MM:SS format.
   * @returns {string} Formatted time string.
   */
  const formatTime = (value) => {
    if (!value || typeof value !== "string" || value.length < 5) return "";

    // Safely extract HH:MM part (first 5 characters)
    const [hourStr, minuteStr] = value.slice(0, 5).split(':');
    let hours = parseInt(hourStr, 10);
    const minutes = minuteStr;

    if (isNaN(hours) || isNaN(parseInt(minutes, 10))) return "";
    const ampm = hours >= 12 ? 'PM' : 'AM';

    // Convert 24-hour time to 12-hour time (0 becomes 12 AM, 13 becomes 1 PM, etc.)
    hours = hours % 12;
    hours = hours ? hours : 12; // The hour '0' should be '12'

    return `${hours}:${minutes} ${ampm}`;
  };

  /**
   * Helper to format phone number components into a standard string.
   * @param {object} phone - Object containing countryCode, areaCode, telPrefix, and lineNum.
   * @returns {string} Formatted phone string.
   */
  const formatPhone = (phone) => {
    if (!phone || !phone.lineNum) return "Phone number not available.";
    const cc = phone.countryCode ? `+${phone.countryCode} ` : "";
    const ac = phone.areaCode ? `(${phone.areaCode}) ` : "";
    return `${cc}${ac}${phone.telPrefix}-${phone.lineNum}`;
  };

  /**
   * Helper to format address components into a single line string.
   * @param {object} address - Object containing addressLine1, addressLine2, city, state, zip.
   * @returns {string} Formatted address string.
   */
  const formatAddress = (address) => {
    if (!address || !address.addressLine1) return "Address not available.";
    const line2 = address.addressLine2 ? `, ${address.addressLine2}` : "";
    return `${address.addressLine1}${line2}, ${address.city}, ${address.state} ${address.zip}`;
  };

  /**
   * Initializes the edit form data structure based on the selected organization details.
   * @param {object} org - The selected organization details object.
   * @returns {object} The initialized form data object.
   */
  const normalizeServiceIds = (services) => {
    if (!Array.isArray(services)) return [];

    return services
      .map((s) => {
        const id = s?.serviceTypeID ?? s?.serviceTypeId ?? s;
        return id == null ? null : String(id);
      })
      .filter((id) => id !== null);
  };

  const getServiceLabels = (services) => {
  const ids = normalizeServiceIds(services);
  return ids.map((id) => serviceTypeList[id]).filter(Boolean);
};

  const initializeEditForm = (org) => {
    const primaryAddress = org.addresses?.[0] || {};
    const primaryPhone = org.phones?.[0] || {};

    // Grouping hours by day name for easier form handling
    const hoursMap = org.hours?.reduce((acc, h) => {
      acc[h.dayName] = { startTime: h.startTime, endTime: h.endTime };
      return acc;
    }, {});

    // Find a single timeZone to use for the whole organization hours block.
    // Use the timeZone from the first hour entry with a valid timeZone, or default to an empty string.
    const initialTimeZone = org.hours?.find(h => h.timeZone)?.timeZone || "";
    
    return {
      name: org.name || "",
      description: org.description || "",
      primaryContactFirstName: org.primaryContactFirstName || "",
      primaryContactLastName: org.primaryContactLastName || "",
      website: org.website || "",
      affiliation: org.affiliation || "",
      email: org.affiliatedUser?.email || "",           // User Email is from the affiliated user
      countryCode: primaryPhone.countryCode || "",      // Phone
      areaCode: primaryPhone.areaCode || "",
      telPrefix: primaryPhone.telPrefix || "",
      lineNum: primaryPhone.lineNum || "",
      addressLine1: primaryAddress.addressLine1 || "",  // Address - We use the first address for the edit form
      addressLine2: primaryAddress.addressLine2 || "",
      city: primaryAddress.city || "",
      state: primaryAddress.state || "",
      zip: primaryAddress.zip || "",
      hours: hoursMap || {},                            // Hours
      timeZone: initialTimeZone,
      services: normalizeServiceIds(org.services),
    };
  };

  // =========================================================================
  // HANDLERS
  // =========================================================================

  const handleToggleService = (serviceTypeID) => {
    const id = String(serviceTypeID);

    setEditFormData((prev) => {
      const existing = Array.isArray(prev.services) ? prev.services.map(String) : [];
      const isChecked = existing.includes(id);

      return {
        ...prev,
        services: isChecked
          ? existing.filter((x) => x !== id)
          : [...existing, id],
      };
    });
  };

  const buildSearchParams = () => {
    const params = {};

    // Name search
    if (searchName.trim()) params.name = searchName.trim();

    // If quickService selected, treat it like a single-service filter
    // (You can also merge it into selectedServices if you prefer)
    const combinedServices = new Set(selectedServices.map(String));
    if (quickService !== "") combinedServices.add(String(quickService));

    const servicesArr = Array.from(combinedServices);
    if (servicesArr.length > 0) {
      // backend supports comma-separated OR repeated query keys
      // easiest is comma-separated:
      params.services = servicesArr.join(",");
      params.match = matchMode; // "any" or "all"
    }

    return params;
  };

  const runSearch = async () => {
    setIsSearching(true);
    setSearchError("");

    try {
      const params = buildSearchParams();

      // If no filters at all, you can either:
      // A) call the normal /organization endpoint (current behavior)
      // B) call /organization/search with no params (if backend supports it)
      const hasAnyFilter =
        (params.name && params.name.length > 0) ||
        (params.services && params.services.length > 0);

      if (!hasAnyFilter) {
        // fallback to original list fetch
        const response = await axios.get(url, { withCredentials: true });
        const orgs = response.data.OrgNames || [];
        orgs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setOrganizations(orgs);
        return;
      }

      const searchUrl = `${baseUrl}/api/foodPantry/organization/search`;

      const response = await axios.get(searchUrl, {
        withCredentials: true,
        params,
      });

      const orgs = response.data.organizations || response.data.OrgNames || [];
      orgs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setOrganizations(orgs);
    } catch (err) {
      console.error("Search error:", err);
      setOrganizations([]);
      setSearchError(
        err?.response?.data?.message || "Search failed. Please try again."
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearFilters = () => {
    setSearchName("");
    setQuickService("");
    setSelectedServices([]);
    setMatchMode("any");
    setSearchError("");
    // re-load full list
    runSearch();
  };

  const toggleFilterService = (serviceId) => {
    const id = String(serviceId);
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /** Handles closing the Organization Details Modal. */
  const handleCloseModal = () => {
    setShowOrgModal(false);
    setSelectedOrgDetails(null);
    setDetailsError("");
  };

  /**
   * Handles selecting an organization from the list.
   * Sets details directly from the pre-fetched organization object.
   * @param {object} org - The organization object from the list.
   */
  const handleSelectClick = (org) => {
    const orgID = org.orgID || org.id;
    if (!orgID) {
      console.error("Organization ID is missing.");
      return;
    }

    // Preserve any external behavior
    onSelect(orgID);

    // Set the details directly from the organization object received from the initial search API call
    setSelectedOrgDetails(org);
    setShowOrgModal(true);
    setLoadingOrgDetails(false); // No loading needed as data is present
    setDetailsError("");
  };

  /** Opens the edit modal and initializes the form. */
  const handleOpenEditModal = () => {
    setUpdateError("");
    if (selectedOrgDetails) {
      setEditFormData(initializeEditForm(selectedOrgDetails));
      setShowEditModal(true);
    }
  };

  /** Closes the edit modal and resets related states. */
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setIsUpdating(false);
    setUpdateError("");

    // Re-initialize state to the safe default structure on close
    setEditFormData({ hours: {}, timeZone: "", services: [] });
  };

  /**
   * Handles changes for general form fields.
   * @param {object} e - The event object.
   */
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Handles changes for days/hours table inputs.
   * @param {string} dayName - The name of the day (e.g., "Monday").
   * @param {string} field - The field to update ("startTime" or "endTime").
   * @param {string} value - The new time value.
   */
  const handleHoursChange = (dayName, field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      hours: {
        ...prev.hours,
        [dayName]: {
          ...(prev.hours[dayName] || { dayName }),
          [field]: value,
        },
      },
    }));
  };

  /**
   * Handles submission for the edit form, performing validation and API call.
   * @param {object} e - The event object.
   */
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrgDetails?.orgID) return;

    // SECURITY CHECK: Re-verify authorization on submit
    if (!canEditOrganization(userRole, userOrgID, selectedOrgDetails)) {
        setUpdateError("You are not authorized to edit this organization.");
        return;
    }

    setIsUpdating(true);
    setUpdateError("");

    try {
      const orgID = selectedOrgDetails.orgID;
      const updateUrl = `${baseUrl}/api/foodPantry/organization/${orgID}`;
      
      // Get the original email for comparison
      const originalEmail = selectedOrgDetails.affiliatedUser?.email;

      // 1. Prepare the Hours data in the required 'days' array format
      // Only include days that have a start or end time set.
      const hoursToUpdateArray = Object.entries(editFormData.hours)
        .filter(([, value]) => value.startTime || value.endTime)
        .map(([dayName, hours]) => ({
          name: dayName,
          startTime: hours.startTime || null, // Allow clearing time
          endTime: hours.endTime || null,   // Allow clearing time
          timeZone: editFormData.timeZone || null,
        }));

      const allowedServiceIds = Object.keys(serviceTypeList); // ["0","1","2","3","4"]
      const cleanServices = (editFormData.services || [])
      .map(String)
      .filter((id) => allowedServiceIds.includes(id));

      // 2. Construct the Single Combined Update Payload
      const combinedUpdatePayload = {
        organization: {
          name: editFormData.name,
          description: editFormData.description,
          primaryContactFirstName: editFormData.primaryContactFirstName,
          primaryContactLastName: editFormData.primaryContactLastName,
          website: editFormData.website,
          affiliation: editFormData.affiliation,
        },
        // Only include the user object if the email has actually changed
        ...(editFormData.email !== originalEmail && {
            user: {
                email: editFormData.email
            }
        }),
        phone: {
          countryCode: editFormData.countryCode,
          areaCode: editFormData.areaCode,
          telPrefix: editFormData.telPrefix,
          lineNum: editFormData.lineNum,
        },
        address: {
          addressLine1: editFormData.addressLine1,
          addressLine2: editFormData.addressLine2,
          city: editFormData.city,
          state: editFormData.state,
          zip: editFormData.zip,
        },
        // 3. Include the consolidated 'days' array in the main payload
        days: hoursToUpdateArray,
        services: cleanServices,
      };

      // 4. Send the Single Combined PUT request
      await axios.put(updateUrl, combinedUpdatePayload, {
        withCredentials: true,
      });

      // --- Success: Update Local State ---
      const updatedDetails = (() => {
          // Start with a new shallow copy of the previous details
          const newDetails = { ...selectedOrgDetails };
          
          // Update details manually to reflect changes immediately
          newDetails.name = editFormData.name;
          newDetails.description = editFormData.description;
          newDetails.primaryContactFirstName = editFormData.primaryContactFirstName;
          newDetails.primaryContactLastName = editFormData.primaryContactLastName;
          newDetails.website = editFormData.website;
          newDetails.affiliation = editFormData.affiliation;
          newDetails.services = cleanServices;
          
          // Update Email (Check)
          if (editFormData.email !== selectedOrgDetails.affiliatedUser?.email) {
              
            // Deep copy the affiliatedUser object and update the email
              newDetails.affiliatedUser = { ...newDetails.affiliatedUser, email: editFormData.email };
          }
          
          // Update Phone (Deep copy the phones array and the first phone object)
          if (newDetails.phones?.[0]) {
              newDetails.phones = [
                { 
                  ...newDetails.phones[0], 
                  countryCode: editFormData.countryCode, 
                  areaCode: editFormData.areaCode, 
                  telPrefix: editFormData.telPrefix, 
                  lineNum: editFormData.lineNum 
                },
                ...newDetails.phones.slice(1) // Keep rest of array
              ];
          }
          
          // Update Address (Deep copy the addresses array and the first address object)
          if (newDetails.addresses?.[0]) {
              newDetails.addresses = [
                { 
                  ...newDetails.addresses[0], 
                  addressLine1: editFormData.addressLine1, 
                  addressLine2: editFormData.addressLine2, 
                  city: editFormData.city, 
                  state: editFormData.state, 
                  zip: editFormData.zip 
                },
                ...newDetails.addresses.slice(1) // Keep rest of array
              ];
          }

          // Update Hours based on the current edit form data and the new timeZone field
          newDetails.hours = Object.entries(editFormData.hours)
            .filter(([, hours]) => hours.startTime || hours.endTime) // Only keep days with hours set
            .map(([dayName, hours]) => ({
              dayName: dayName,
              startTime: hours.startTime,
              endTime: hours.endTime,
              timeZone: editFormData.timeZone || null,
          }));

          return newDetails;
      })();
      
      // A. Update the currently selected organization details state
      setSelectedOrgDetails(updatedDetails);

      // B. Update the main organizations list
      setOrganizations(prevOrgs => {
          const orgID = selectedOrgDetails.orgID;
          const index = prevOrgs.findIndex(org => org.orgID === orgID);

          if (index !== -1) {
              const newOrgs = [...prevOrgs];
              newOrgs[index] = updatedDetails; // Use the fully updated object
              return newOrgs;
          }
          return prevOrgs;
      });

      // C. Close the modal
      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating organization details", error);
      setUpdateError("Failed to update organization details. Please check your input and try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  // =========================================================================
  // DISPLAY CALCULATIONS
  // =========================================================================

  // Derive necessary display values
  const primaryEmail = selectedOrgDetails?.affiliatedUser?.email;
  const primaryPhone = selectedOrgDetails?.phones?.[0];
  const primaryAddress = selectedOrgDetails?.addresses?.[0];

  // Combine first and last name for display, filtering out null/empty strings
  const contactName = [
    selectedOrgDetails?.primaryContactFirstName,
    selectedOrgDetails?.primaryContactLastName
  ].filter(Boolean).join(" ");
  const website = selectedOrgDetails?.website;
  const affiliation = selectedOrgDetails?.affiliation;
  // Get a time zone for display (uses the first available one)
  const displayedTimeZone = selectedOrgDetails?.hours?.find(h => h.timeZone)?.timeZone;
  // --------------------------

  // Authorization variable for button rendering. Check is run only when auth is not loading.
  const isEditable = !authLoading && canEditOrganization(userRole, userOrgID, selectedOrgDetails);

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <React.Fragment>
      {/* Main card container for Organization Search */}
      <div className="card col-12 col-md-10 offset-0 offset-md-1 mt-5 card-body container my-3">
        <div className="card-header justify-content-between align-items-center pb-3">
          <h3>
            <span>
              <i className="bi bi-search pe-3"></i>
              Organization Search
            </span>
          </h3>

              {/* SEARCH + FILTER CONTROLS */}
<div className="row g-2 align-items-end pt-4">
  {/* Search by name */}
  <div className="col-12 col-md-5">
    <label className="form-label fw-semibold">Search by organization name</label>
    <input
      type="text"
      className="form-control"
      placeholder="e.g., Community Food Bank"
      value={searchName}
      onChange={(e) => setSearchName(e.target.value)}
    />
  </div>

  {/* Quick filter by service */}
  <div className="col-12 col-md-3">
    <label className="form-label fw-semibold">Quick service search</label>
    <select
      className="form-select"
      value={quickService}
      onChange={(e) => setQuickService(e.target.value)}
    >
      <option value="">Any</option>
      {Object.entries(serviceTypeList).map(([id, label]) => (
        <option key={id} value={id}>{label}</option>
      ))}
    </select>
  </div>

  {/* Any/All */}
  <div className="col-12 col-md-2">
    <label className="form-label fw-semibold">Match</label>
    <select
      className="form-select"
      value={matchMode}
      onChange={(e) => setMatchMode(e.target.value)}
    >
      <option value="any">Any</option>
      <option value="all">All</option>
    </select>
  </div>

  {/* clear buttons */}
  <div className="col-12 col-lg-2 d-flex gap-2">
    <button
      type="button"
      className="btn btn-primary w-100"
      onClick={handleClearFilters}
      disabled={isSearching}
    >
      Clear
    </button>
  </div>
</div>

{/* Custom service multi-select */}
<div className="row pt-3">
  <div className="col-12">
    <label className="form-label fw-semibold mb-2">Filter by multiple services</label>
    <div className="d-flex flex-wrap gap-3">
      {Object.entries(serviceTypeList).map(([id, label]) => (
        <div className="form-check" key={id}>
          <input
            className="form-check-input"
            type="checkbox"
            id={`filter-svc-${id}`}
            checked={selectedServices.includes(String(id))}
            onChange={() => toggleFilterService(id)}
          />
          <label className="form-check-label" htmlFor={`filter-svc-${id}`}>
            {label}
          </label>
        </div>
      ))}
    </div>
    <div className="text-muted small mt-2">
      Match mode applies to the selected services (plus the quick service, if chosen).
    </div>
  </div>
    {/*Search button */}
  <button
      type="button"
      className="btn btn-primary w-100"
      onClick={runSearch}
      disabled={isSearching}
    >
      {isSearching ? "Searching..." : "Search"}
    </button>
</div>

{searchError && (
  <div className="alert alert-danger mt-3 mb-0">
    {searchError}
  </div>
)}


          {/* Organization list */}
          <ul className="list-group w-100 pt-5">
 
            {organizations.map((org, index) => (
              <li key={index} className="list-group-item">
                <div className="row">
                  <div className="col-10 row d-flex align-items-center">
                    <p className="col-8 mb-0 fw-semibold">{org.name}</p>
        
                    <button
                      type="button"
                      className="col-12 col-md-4 btn btn-primary"
         
                      onClick={() => handleSelectClick(org)}
                    >
                      Select
                    </button>
                  </div>
     
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      
      {/* Organization Details Modal */}
      <Modal show={showOrgModal} onHide={handleCloseModal} size="lg" centered>
     
        <div className="card shadow-lg">
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center flex-grow-1">
              <h3 className="mb-0">
                <span>
                  <i className="bi bi-building me-2"></i>
                  {selectedOrgDetails?.name || "Organization Details"}
                </span>
              </h3>
            </Modal.Title>
            {/* CONDITIONAL RENDERING OF EDIT BUTTON based on authorization and loading state */}
            {selectedOrgDetails && (
              authLoading ? ( 
  
               // Show a small spinner if auth is loading after selecting an org
                <div className="spinner-border spinner-border-sm ms-3" role="status">
                    <span className="visually-hidden">Loading permissions...</span>
                </div>
              ) : 
              (
                // Show button only if not loading AND user has permission to edit
                isEditable && ( 
                  <Button
                    variant="primary"
          
                    className="ms-3 me-3"
                    onClick={handleOpenEditModal}
                  >
                    <i className="bi bi-pencil-square me-2"></i>Edit Details
                  </Button>
          
                )
              )
            )}
          </Modal.Header>

          <Modal.Body>
            {loadingOrgDetails && (
              <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {detailsError && (
              <div className="alert alert-danger text-center">{detailsError}</div>
            )}

            {selectedOrgDetails && (
              <div>
                {/* Organization Details (Uses flexbox/row layout) */}
                <div className="row mb-4">
                  {/* Name */}
                  <div className="col-12 col-md-6 mb-3">
                    <h5 className="fw-bold mb-1">
                      <i className="bi bi-building me-2"></i> Organization Name
                    </h5>
                    <p className="text-secondary">{selectedOrgDetails.name}</p>
                  </div>
                </div>

                <div className="row mb-4">
                  {/* Description */}
                  <div className="col-12">
                    <h5 className="fw-bold mb-1 me-2">
                      <i className="bi bi-journal-text me-2"></i>
                      Description
                    </h5>
                    <p className="text-secondary">
                      {selectedOrgDetails.description || "No description provided."}
                    </p>
                  </div>
                </div>

                {/* --- Affiliation & Website (MOVED HERE) --- */}
                {/* Conditionally render this row ONLY if Affiliation or Website has a value */}
                {(website || affiliation) && (
                  <div className="row mb-4">
                    {/* Affiliation (Only shows if value exists) */}
                    {affiliation && (
                      <div className="col-12 col-md-6 mb-3">
                        <h5 className="fw-bold mb-1">
                          <i className="bi bi-diagram-3 me-2"></i> Affiliation
                        </h5>
                        <p className="text-secondary">{affiliation}</p>
                      </div>
                    )}
                    {/* Website URL (Only shows if value exists) */}
                    {website && (
                      <div className="col-12 col-md-6 mb-3">
                        <h5 className="fw-bold mb-1">
                          <i className="bi bi-globe me-2"></i> Website
                        </h5>
                        <p className="text-secondary">
                          <a
                            href={website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary"
                          >
                            {website}
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <hr />

                {/* CONTACT INFORMATION ROW A: Contact Name & Email (col-md-6 layout) */}
                <div className="row mb-4">
                  {/* Contact Name (Only shows if combined value exists) */}
                  {contactName.trim() !== "" && (
                    <div className="col-12 col-md-6 mb-3">
                      <h5 className="fw-bold mb-1">
                        <i className="bi bi-person me-2"></i> Contact Name
                      </h5>
                      <p className="text-secondary">{contactName}</p>
                    </div>
                  )}
                  {/* Email */}
                  <div className="col-12 col-md-6 mb-3">
                    <h5 className="fw-bold mb-1">
                      <i className="bi bi-envelope me-2"></i> Email
                    </h5>
                    <p className="text-secondary">
                      <a
                        href={`mailto:${primaryEmail}`}
                        className="text-decoration-none text-primary"
                      >
                        {primaryEmail || "Email not available."}
                      </a>
                    </p>
                  </div>
                </div>

                {/* CONTACT INFORMATION ROW B: Address & Phone (col-md-6 layout) */}
                <div className="row mb-4">
                  {/* Address */}
                  <div className="col-12 col-md-6 mb-3">
                    <h5 className="fw-bold mb-1">
                      <i className="bi bi-geo-alt me-2"></i> Address
                    </h5>
                    <p className="text-secondary">
                      {formatAddress(primaryAddress)}
                    </p>
                  </div>
                  {/* Phone Number */}
                  <div className="col-12 col-md-6 mb-3">
                    <h5 className="fw-bold mb-1">
                      <i className="bi bi-phone me-2"></i> Phone Number
                    </h5>
                    <p className="text-secondary">
                      {" "}
                      {primaryPhone ? (
                        <a
                          href={`tel:${formatPhone(primaryPhone)}`}
                          className="text-decoration-none text-primary"
                        >
                          {formatPhone(primaryPhone)}
                        </a>
                      ) : (
                        "Phone number not available."
                      )}{" "}
                    </p>
                  </div>
                </div>

                <hr />

                {/* Services Offered */}
                <div className="row mb-4">
                  <div className="col-12">
                    <h5 className="fw-bold mb-1">
                      <i className="bi bi-bag-check me-2"></i> Services Offered
                    </h5>

                    {(() => {
                      const labels = getServiceLabels(selectedOrgDetails?.services);

                      if (!labels || labels.length === 0) {
                        return <p className="text-secondary">No Services available.</p>;
                      }

                      return (
                        <ul className="text-secondary mb-0">
                          {labels.map((label) => (
                            <li key={label}>{label}</li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>
                </div>

                {/* Hours of Operation (Uses flexbox/row layout) */}
                <div className="row mb-4">
                  <div className="col-12">
                    <h5 className="fw-bold mb-1">
                      <i className="bi bi-clock me-2"></i> Hours of Operation
                    </h5>

                    {/* Display the Time Zone for the hours */}
                    {displayedTimeZone && (
                      <p className="text-secondary">All hours are displayed in <strong>{displayedTimeZone}</strong>.</p>
                    )}

                    {selectedOrgDetails.hours && selectedOrgDetails.hours.length > 0 ? (
                      <div className="table-responsive overflow-auto" style={{ maxHeight: "300px" }}>
                        <table className="table table-sm table-borderless text-secondary border-0">
                          <tbody>
                            {/* Sort by day of week for readability */}
                            {selectedOrgDetails.hours
                              .sort((a, b) => {
                                const daysOrder = [
                                  "Sunday",
                                  "Monday",
                                  "Tuesday",
                                  "Wednesday",
                                  "Thursday",
                                  "Friday",
                                  "Saturday",
                                ];
                                return (
                                  daysOrder.indexOf(a.dayName) - daysOrder.indexOf(b.dayName)
                                );
                              })
                              .map((h, idx) => (
                                <tr key={idx}>
                                  <td className="fw-semibold ps-0 py-1 text-dark" style={{ width: "30%" }}>
                                    {h.dayName}:
                                  </td>
                                  {/* Time is formatted to 12-hour AM/PM */}
                                  <td className="text-dark py-1">
                                    {formatTime(h.startTime)} - {formatTime(h.endTime)}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-secondary">Hours not available.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
          </Modal.Footer>
        </div>
      </Modal>

      {/* NESTED EDIT MODAL */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg" centered >
        <div className="card shadow-lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-pencil-square me-2"></i>Edit Organization Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
    
            <form onSubmit={handleEditSubmit}>
              {/* Name and Description (Flexbox/Row layout) */}
              <div className="row mb-3">
                <div className="col-md-6 mb-3">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="name"
       
                      name="name"
                      placeholder="Organization Name" // Smart default
                      value={editFormData.name || ''}
                      onChange={handleFormChange}
                      required
                    />
                    <label htmlFor="name">Organization Name</label>
              
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="form-floating">
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      placeholder="Organization Description" // Smart default

                      value={editFormData.description || ''}
                      onChange={handleFormChange}
                      rows="1"
                      required
                      style={{ height: '58px' }} // Adjusted height for textarea in form-floating
                    />
                    <label htmlFor="description">Description</label>
                  </div>
                </div>
              </div>
             
              {/* --- Affiliation & Website --- */}
              <div className="row mb-3">
                {/* Affiliation */}
                <div className="col-md-6 mb-3">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="affiliation"
                      name="affiliation"
                      placeholder="Affiliation"
                      value={editFormData.affiliation || ''}
                      onChange={handleFormChange}
                    />
                    <label htmlFor="affiliation"><i className="bi bi-diagram-3 me-2"></i>Affiliation</label>
                  </div>
                </div>
                {/* Website URL */}
                <div className="col-md-6 mb-3">
                  <div className="form-floating">
                    <input
                      type="url"
                      className="form-control"
                      id="website"
                      name="website"
                      placeholder="https://example.com"
                      value={editFormData.website || ''}
                      onChange={handleFormChange}
                    />
                    <label htmlFor="website"><i className="bi bi-globe me-2"></i>Website URL</label>
                  </div>
                </div>
              </div>

              {/* ---------------------------------------------------- */}
              <h5 className="fw-bold my-3">Contact Information</h5>
              <hr className="mt-0" />
              {/* Contact First Name and Last Name (Flexbox/Row layout) */}
              <div className="row mb-3">
                {/* Contact First Name */}
                <div className="col-md-6 mb-3">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="primaryContactFirstName"
                      name="primaryContactFirstName"
                      placeholder="First Name"
                      value={editFormData.primaryContactFirstName || ''}
                      onChange={handleFormChange}
                    />
                    <label htmlFor="primaryContactFirstName"><i className="bi bi-person me-2"></i>Contact First Name</label>
                  </div>
                </div>
                {/* Contact Last Name */}
                <div className="col-md-6 mb-3">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="primaryContactLastName"
                      name="primaryContactLastName"
                      placeholder="Last Name"
                      value={editFormData.primaryContactLastName || ''}
                      onChange={handleFormChange}
                    />
                    <label htmlFor="primaryContactLastName">Contact Last Name</label>
                  </div>
                </div>
              </div>

              {/* Email and Phone (Flexbox/Row layout) */}
              <div className="row mb-3 d-flex align-items-center">
                {/* Aligns the column content (email vs phone) */}
                <div className="col-md-6 mb-3">
                  <div className="form-floating">
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      name="email"
                      placeholder="email@example.com"
                      value={editFormData.email || ''}
                      onChange={handleFormChange}
                      required
                    />
                    <label htmlFor="email"><i className="bi bi-envelope me-2"></i>Email</label>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  {/* Phone Number Fields: Replaced input-group with d-flex for cleaner internal alignment of form-floating components */}
                  <div className="d-flex">
                    <div className="form-floating me-1 flex-fill">
                      <input
                        type="text"
                        className="form-control"
                        id="countryCode"
                        name="countryCode"
                        placeholder="1"
                        value={editFormData.countryCode || ''}
                        onChange={handleFormChange}
                        maxLength="3"
                      />
                      <label htmlFor="countryCode" className="text-secondary">CC</label>
                    </div>
                    <div className="form-floating me-1 flex-fill">
                      <input
                        type="text"
                        className="form-control"
                        id="areaCode"
                        name="areaCode"
                        placeholder="555"
                        value={editFormData.areaCode || ''}
                        onChange={handleFormChange}
                        maxLength="3"
                      />
                      <label htmlFor="areaCode" className="text-secondary">Area Code</label>
                    </div>
                    <div className="form-floating me-1 flex-fill">
                      <input
                        type="text"
                        className="form-control"
                        id="telPrefix"
                        name="telPrefix"
                        placeholder="555"
                        value={editFormData.telPrefix || ''}
                        onChange={handleFormChange}
                        maxLength="3"
                      />
                      <label htmlFor="telPrefix" className="text-secondary">Prefix</label>
                    </div>
                    <div className="form-floating flex-fill">
                      <input
                        type="text"
                        className="form-control"
                        id="lineNum"
                        name="lineNum"
                        placeholder="1212"
                        value={editFormData.lineNum || ''}
                        onChange={handleFormChange}
                        maxLength="4"
                      />
                      <label htmlFor="lineNum" className="text-secondary">Line</label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Fields */}
              <h5 className="fw-bold my-3">Address</h5>
              <hr className="mt-0" />
              <div className="row mb-3">
                <div className="col-md-6 mb-3">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="addressLine1"
                      name="addressLine1"
                      placeholder="Street Address"
                      value={editFormData.addressLine1 || ''}
                      onChange={handleFormChange}
                      required
                    />
                    <label htmlFor="addressLine1"><i className="bi bi-house me-2"></i>Address Line 1</label>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="addressLine2"
                      name="addressLine2"
                      placeholder="Apt, Suite, Unit"
                      value={editFormData.addressLine2 || ''}
                      onChange={handleFormChange}
                    />
                    <label htmlFor="addressLine2">Address Line 2 (Optional)</label>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="city"
                      name="city"
                      placeholder="City"
                      value={editFormData.city || ''}
                      onChange={handleFormChange}
                      required
                    />
                    <label htmlFor="city">City</label>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="state"
                      name="state"
                      placeholder="State/Province"
                      value={editFormData.state || ''}
                      onChange={handleFormChange}
                      maxLength="2"
                      required
                    />
                    <label htmlFor="state">State</label>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="zip"
                      name="zip"
                      placeholder="Zip Code"
                      value={editFormData.zip || ''}
                      onChange={handleFormChange}
                      maxLength="10"
                      required
                    />
                    <label htmlFor="zip">Zip Code</label>
                  </div>
                </div>
              </div>
              
              <h5 className="fw-bold my-3">Services Offered</h5>
              <hr className="mt-0" />

              <div className="row mb-3">
                <div className="col-12">
                  {Object.entries(serviceTypeList).map(([id, label]) => (
                    <div className="form-check" key={id}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`svc-${id}`}
                        checked={(editFormData.services || []).map(String).includes(String(id))}
                        onChange={() => handleToggleService(id)}
                      />
                      <label className="form-check-label" htmlFor={`svc-${id}`}>
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>


              <h5 className="fw-bold my-3">Days & Hours</h5>
              <hr className="mt-0" />

              {/* Time Zone Input Field for hours of operation */}
              <div className="row mb-3">
                <div className="col-md-6 mb-3">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="timeZone"
                      name="timeZone"
                      placeholder="e.g. CST, EST, PST, GMT"
                      value={editFormData.timeZone || ''}
                      onChange={handleFormChange}
                      maxLength="10"
                    />
                    <label htmlFor="timeZone"><i className="bi bi-globe me-2"></i>Time Zone (e.g., CST)</label>
                  </div>
                </div>
              </div>
          
     
              {/* Days/Hours (Scrollable Table) */}
              <div className="table-responsive overflow-auto" style={{ maxHeight: "300px" }}>
                <table className="table table-sm table-striped">
                
                  <thead>
                    <tr>
                      <th style={{ width: "30%" }}>Day</th>
                      <th style={{ width: "35%" }}>Start Time</th>
                      <th style={{ width: "35%" }}>End Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(dayName => (
                      <tr key={dayName}>
                        <td className="fw-semibold align-middle">{dayName}</td>
                        <td>
                          <input
               
                            type="time"
                            className="form-control form-control-sm"
                            value={editFormData.hours?.[dayName]?.startTime || ''}
                            onChange={(e) => handleHoursChange(dayName, 'startTime', e.target.value)}
                          />
                        </td>
                  
                        <td>
                          <input
                            type="time"
                            className="form-control form-control-sm"
           
                            value={editFormData.hours?.[dayName]?.endTime || ''}
                            onChange={(e) => handleHoursChange(dayName, 'endTime', e.target.value)}
                          />
                        </td>
                  
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted small mt-2">Set times to update the hours for a specific day. Leave both time fields blank to clear existing hours for a day.</p>

              {updateError && <div className="alert alert-danger mt-3">{updateError}</div>}

              <div className="d-flex justify-content-end pt-3">
                <Button variant="secondary" onClick={handleCloseEditModal} className="me-2" disabled={isUpdating}>
                  Cancel
               
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isUpdating}
                >
      
                  {isUpdating ?
                  (
                    <React.Fragment>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </React.Fragment>
                  ) : (
                    <React.Fragment><i className="bi bi-save me-2"></i>Save Changes</React.Fragment>
                  )}
                </Button>
              </div>
            </form>
       
          </Modal.Body>
          <Modal.Footer>
          </Modal.Footer>
        </div>
      </Modal>
    </React.Fragment>
  );
}

export default OrganizationList;